import React, { useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import { MenuItem, Select } from "@curves/ui-base";
import { refIsRequiredError } from "@util/utils";

const fakeSortOptions = [
    {
        text: '建立日期新→舊',
        value: 1,
    },
    {
        text: '建立日期舊→新',
        value: 2,
    },
    {
        text: '金額高→低',
        value: 3,
    },
    {
        text: '金額低→高',
        value: 4,
    },
];

/**
 * PageSortAndTotalBox
 *
 * 用於顯示分頁資訊（如：目前頁碼、總筆數）與排序選單的元件。
 * 適用於總覽列表上方或右上角的控制列。
 *
 * @component
 * @param {object} props - 組件參數
 * @param {string} [props.className] - 額外的 CSS class（通常用來排版）
 * @param {number} [props.pageSize=10] - 每頁顯示筆數
 * @param {number} [props.pageIndex=1] - 當前頁碼（從 1 開始）
 * @param {number} [props.totalCount=0] - 總筆數
 * @param {string|number} [props.sortDefaultValue] - 排序選單的預設值
 * @param {Array<{text: string, value: string|number}>} [props.sortOptions=fakeSortOptions] - 排序選項列表
 * @param {(value: string|number) => void} [props.sortOnChange] - 排序選單變更時的 callback
 * @param {boolean} [props.isSort=false] - 是否顯示排序選單
 * @param {boolean} [props.isCurrentPageFirstIndex=false] - 是否顯示目前頁的起始筆數
 * @param {boolean} [props.isCurrentPageLastIndex=false] - 是否顯示目前頁的結束筆數
 * @param {React.Ref} ref - 提供外部調用的方法（getResult, isError）
 *
 * @returns {JSX.Element}
 *
 * @example
 * <PageSortAndTotalBox
 *   pageSize={20}
 *   pageIndex={2}
 *   totalCount={58}
 *   isSort={true}
 *   sortDefaultValue="latest"
 *   sortOptions={[{ text: '最新', value: 'latest' }, { text: '最舊', value: 'oldest' }]}
 *   sortOnChange={(value) => console.log(value)}
 *   isCurrentPageFirstIndex
 *   isCurrentPageLastIndex
 * />
 *
 * @example
 * // 使用 ref 呼叫內部方法
 * const boxRef = useRef();
 * boxRef.current.getResult(); // 取得排序結果
 * boxRef.current.isError();   // 檢查是否有錯誤
 */
const PageSortAndTotalBox = React.forwardRef((props, ref) => {
    const {
        className,
        pageSize = 10,
        pageIndex = 1,
        totalCount = 0,
        sortDefaultValue,
        sortOptions = fakeSortOptions,
        sortOnChange,
        isSort = false,
        isCurrentPageFirstIndex = false,
        isCurrentPageLastIndex = false,
    } = props;

    const sortRef = useRef();

    // 取得頁數資訊
    const getPageInfoText = () => {
        if (!isCurrentPageFirstIndex && !isCurrentPageLastIndex) return '';

        const start = isCurrentPageFirstIndex ? pageSize * (pageIndex - 1) + 1 : null;
        const end = isCurrentPageLastIndex ? Math.min(pageSize * pageIndex, totalCount) : null;

        if (start && end) return `${start} - ${end}筆`;
        if (start) return `${start}筆`;
        if (end) return `${end}筆`;

        return '';
    };

    const handleSortChange = (_, __, value) => {
        if (typeof sortOnChange === 'function') sortOnChange(value);
    };

    useImperativeHandle(
        ref,
        () => ({
            getResult: () => sortRef.current?.getResult?.(),
            isError: () => refIsRequiredError(sortRef),
        }),
        // eslint-disable-next-line
        [],
    );

    return (
        <div className={clsx('ml-auto', className)}>
            {totalCount > 0 && <span className={'mr-1'}>{getPageInfoText()}</span>}
            <span className={isSort ? 'mr-2' : ''}>{`(共${totalCount}筆)`}</span>

            {isSort && (
                <Select key={sortDefaultValue} ref={sortRef} defaultValue={sortDefaultValue} onChange={handleSortChange}>
                    {Array.isArray(sortOptions) &&
                        sortOptions.map(({ text, value }) => (
                            <MenuItem key={value} value={value}>
                                {text}
                            </MenuItem>
                        ))}
                </Select>
            )}
        </div>
    );
});

export default PageSortAndTotalBox;
